import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, hasRole, isAdmin } from "../context/AuthContext";
import { api, extractError } from "../lib/api";
import { PageHeader, SectionCard, Pill } from "../components/Ui";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Wand2, BookOpen, ShieldCheck } from "lucide-react";
import DataTableBlock, { dataTableListTitle } from "../components/DataTableBlock";
import DataTableExportButton from "../components/DataTableExportButton";
import { useBootstrapList } from "../hooks/useBootstrapList";
import { useBootstrapWS } from "../hooks/useBootstrapWS";
import { LIST_PAGE_SIZE_OPTIONS } from "../lib/listPagination";
import { PARTY_COLUMNS } from "./partyColumns";
import AdminEntityActions from "../components/AdminEntityActions";

const KIND_LABEL = {
  FARMER: "Farmer",
  BROKER: "Broker",
  DEALER: "Dealer",
  BUYER: "Buyer",
  SUPPLIER: "Supplier",
  LABOUR_CONTRACTOR: "Labour contractor",
  TRANSPORTER: "Transporter / truck owner",
  OTHER: "Other",
};

function summarizeCleanup(data) {
  const vehicle = data?.vehicle_clones || {};
  const supplier = data?.supplier_twins || {};
  const numbers = data?.vehicle_numbers || {};
  const vehicleGroups = vehicle.groups || [];
  const supplierGroups = supplier.groups || [];
  const lines = [];
  const gpN = numbers.gate_passes_updated || 0;
  const slipN = numbers.freight_slips_updated || 0;
  if (gpN || slipN) {
    lines.push(`Vehicle numbers normalized: ${gpN} gate pass(es), ${slipN} freight slip(s).`);
  }
  if (vehicleGroups.length) {
    lines.push(`Vehicle-named clones: ${vehicle.parties_soft_deleted || 0} to remove across ${vehicle.groups_merged || 0} owner(s).`);
  }
  if (supplierGroups.length) {
    lines.push(`Supplier↔transporter twins: ${supplier.parties_soft_deleted || 0} transporter(s) → fold into supplier.`);
    supplierGroups.slice(0, 8).forEach((g) => {
      lines.push(`  · ${g.survivor_name} ← ${(g.merged_transporter_names || []).join(", ")}`);
    });
    if (supplierGroups.length > 8) lines.push(`  · …and ${supplierGroups.length - 8} more`);
  }
  if (!lines.length) lines.push("Nothing to clean — already tidy.");
  return lines.join("\n");
}

export default function PartiesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canUse = hasRole(user, ["admin", "accounts", "accounts_manager"]);
  const adminUser = isAdmin(user);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const list = useBootstrapList({
    path: "/parties/bootstrap",
    columns: PARTY_COLUMNS,
    defaultSort: { id: "display_name", desc: false },
    extraParams: { active: "0" },
    enabled: canUse,
    wsTypes: ["party"],
  });

  useBootstrapWS(["party"], list.reload);

  const remove = async (p) => {
    if (!window.confirm(`Delete party “${p.display_name}”?`)) return;
    try {
      await api.delete(`/parties/${p.id}`);
      toast.success("Party deleted");
      list.reload();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const runCleanup = async (dryRun) => {
    if (!adminUser) return;
    if (!dryRun) {
      const ok = window.confirm(
        "Apply transporter cleanup on this server?\n\n"
        + "• Soft-deletes Owner–Vehicle transporter clones\n"
        + "• Merges matching transporter into supplier (adds transporter role)\n"
        + "• Rewires freight / GL references\n\n"
        + "Preview first if you have not.",
      );
      if (!ok) return;
    }
    try {
      setCleanupBusy(true);
      const { data } = await api.post("/parties/transporter/cleanup", {
        dry_run: dryRun,
        include_vehicle_numbers: true,
        include_vehicle_clones: true,
        include_supplier_twins: true,
      });
      setCleanupPreview(data);
      const summary = summarizeCleanup(data);
      if (dryRun) {
        toast.message("Cleanup preview", { description: summary, duration: 12000 });
      } else {
        toast.success(`Cleanup applied — ${data.parties_soft_deleted || 0} parties archived`);
        list.reload();
      }
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setCleanupBusy(false);
    }
  };

  if (!canUse) {
    return (
      <div className="space-y-6 px-1">
        <PageHeader eyebrow="Master data" title="Parties" description="Restricted to admin and accounts." />
        <p className="text-sm text-muted-foreground">You do not have access to party master data.</p>
      </div>
    );
  }

  const listTitle = dataTableListTitle("parties", list.meta.count, {
    filtered: list.table.rows.length,
    hasFilters: list.table.hasActiveFilters,
  });

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="parties-page">
      <PageHeader
        eyebrow="Master data"
        title="Parties"
        description="Brokers, dealers, suppliers, and other parties. Farmers are created at the gate. Supplier parties can carry purchase deduction rules."
        actions={
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            {adminUser ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  disabled={cleanupBusy}
                  onClick={() => runCleanup(true)}
                  data-testid="party-cleanup-preview"
                >
                  {cleanupBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Wand2 className="h-4 w-4 mr-1" />}
                  Preview cleanup
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={cleanupBusy || !cleanupPreview}
                  onClick={() => runCleanup(false)}
                  data-testid="party-cleanup-apply"
                  title={!cleanupPreview ? "Run Preview cleanup first" : "Apply cleanup on this server"}
                >
                  Apply cleanup
                </Button>
              </>
            ) : null}
            <Button onClick={() => navigate("/parties/new")} className="bg-bansal-sage hover:bg-bansal-sageDark text-white w-full sm:w-auto" data-testid="party-new-button">
              <Plus className="h-4 w-4 mr-1" /> New party
            </Button>
          </div>
        }
      />

      {adminUser && cleanupPreview ? (
        <SectionCard title={cleanupPreview.dry_run ? "Cleanup preview" : "Cleanup result"}>
          <pre className="text-xs whitespace-pre-wrap text-muted-foreground leading-relaxed font-sans">
            {summarizeCleanup(cleanupPreview)}
          </pre>
          <p className="text-[11px] text-muted-foreground mt-2">
            Safe to re-run — already-clean data is a no-op. Soft-deletes only (recoverable).
          </p>
        </SectionCard>
      ) : null}

      <SectionCard title={`Party register (${listTitle})`}>
        <DataTableBlock
          title=""
          wrapInSectionCard={false}
          columns={PARTY_COLUMNS}
          table={list.table}
          loading={list.loading}
          totalCount={list.meta.count}
          testId="parties-filters"
          showBody={false}
          showHint
          pagination={{ ...list.pagination, pageSizeOptions: LIST_PAGE_SIZE_OPTIONS }}
        />

        <div className="md:hidden space-y-3 mt-4">
          {list.table.rows.map((p) => (
            <div key={p.id} className="rounded-lg border p-4 space-y-2 bg-card shadow-sm">
              <div className="flex justify-between gap-2">
                <span className="font-medium">{p.display_name}</span>
                {p.is_active ? <Pill tone="success">Active</Pill> : <Pill tone="default">Inactive</Pill>}
              </div>
              <div className="text-xs text-muted-foreground">{KIND_LABEL[p.kind] || p.kind}</div>
              <div className="text-xs">{p.phone || "—"} · {p.city || "—"}</div>
              <div className="flex gap-2 pt-2 flex-wrap">
                <Button size="sm" variant="outline" className="flex-1" asChild>
                  <Link to={`/parties/${p.id}/edit`}>Edit</Link>
                </Button>
                <Button size="sm" variant="secondary" className="flex-1" asChild>
                  <Link to={`/parties/${p.id}/ledger`} data-testid={`party-ledger-${p.id}`}>
                    <BookOpen className="h-3 w-3 mr-1 inline" /> Ledger
                  </Link>
                </Button>
                <Button size="sm" variant="outline" className="shrink-0" asChild>
                  <Link to={`/parties/${p.id}/ledger?tab=tds`} data-testid={`party-tds-${p.id}`}>
                    <ShieldCheck className="h-3 w-3 mr-1 inline" /> TDS
                  </Link>
                </Button>
                <Button size="sm" variant="ghost" className="text-red-600 shrink-0" onClick={() => remove(p)} aria-label="Archive party">
                  <Trash2 className="h-4 w-4" />
                </Button>
                <AdminEntityActions
                  entityType="party"
                  entityId={p.id}
                  entityLabel={p.display_name}
                  onDeleted={list.reload}
                />
              </div>
            </div>
          ))}
          {list.table.rows.length === 0 && (
            <p className="text-center py-8 text-muted-foreground text-sm">
              {list.table.hasActiveFilters ? "No parties match your filters." : "No parties yet."}
            </p>
          )}
        </div>

        <div className="hidden md:block overflow-x-auto mt-4">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/50">
                <th className="px-3 py-2">Kind</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Phone</th>
                <th className="px-3 py-2">City</th>
                <th className="px-3 py-2">GSTIN</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {list.table.rows.map((p) => (
                <tr key={p.id} className="border-t hover:bg-accent/60">
                  <td className="px-3 py-2.5">
                    <span className="text-xs">{KIND_LABEL[p.kind] || p.kind}</span>
                  </td>
                  <td className="px-3 py-2.5 font-medium">{p.display_name}</td>
                  <td className="px-3 py-2.5">{p.phone || "—"}</td>
                  <td className="px-3 py-2.5">{p.city || "—"}</td>
                  <td className="px-3 py-2.5 font-mono text-xs">{p.gstin || "—"}</td>
                  <td className="px-3 py-2.5">
                    {p.is_active ? <Pill tone="success">Active</Pill> : <Pill tone="default">Inactive</Pill>}
                  </td>
                  <td className="px-3 py-2.5 text-right space-x-1 whitespace-nowrap">
                    <Button size="sm" variant="outline" asChild>
                      <Link to={`/parties/${p.id}/edit`} data-testid={`party-edit-${p.id}`}>
                        <Pencil className="h-3 w-3 mr-1 inline" /> Edit
                      </Link>
                    </Button>
                    <Button size="sm" variant="secondary" asChild>
                      <Link to={`/parties/${p.id}/ledger`} data-testid={`party-ledger-${p.id}`}>
                        <BookOpen className="h-3 w-3 mr-1 inline" /> Ledger
                      </Link>
                    </Button>
                    <Button size="sm" variant="outline" asChild>
                      <Link to={`/parties/${p.id}/ledger?tab=tds`} data-testid={`party-tds-${p.id}`}>
                        <ShieldCheck className="h-3 w-3 mr-1 inline" /> TDS
                      </Link>
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(p)} data-testid={`party-del-${p.id}`} aria-label="Archive party">
                      <Trash2 className="h-3 w-3" />
                    </Button>
                    <AdminEntityActions
                      entityType="party"
                      entityId={p.id}
                      entityLabel={p.display_name}
                      onDeleted={list.reload}
                    />
                  </td>
                </tr>
              ))}
              {list.table.rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-10 text-muted-foreground">
                    {list.table.hasActiveFilters ? "No parties match your filters." : "No parties yet."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex justify-end">
          <DataTableExportButton
            apiPath="/parties"
            params={list.exportParams}
            columns={PARTY_COLUMNS}
            filename="parties.xlsx"
            testId="parties-export"
          />
        </div>
      </SectionCard>
    </div>
  );
}
